import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { UserBubble } from '~/ui/domain/chat/UserBubble';

import type { QuestionnaireQuestion } from '../../../data-contract/chat';
import { serializeRichTextToMarkdown } from '../../markdown-mentions';
import { parseMentionMarkup } from '../../mention-markup';
import { useChatStore } from '../../stores/chat-open-store';
import { MentionMarkdown } from './MentionMarkdown';

export type LocalAnswers = Readonly<Record<string, string>> | undefined;

// A question's effective answer: the backend-echoed answer wins, otherwise the
// pending selection the user made in the composer (held in the chat store).
export const mergedAnswer = (question: QuestionnaireQuestion, local: LocalAnswers): string | undefined =>
    is.nonEmptyString(question.answer) ? question.answer : local?.[question.actionId];

// The first question still awaiting an answer, or undefined once all are answered.
export const currentQuestion = (
    questions: readonly QuestionnaireQuestion[],
    local: LocalAnswers,
): QuestionnaireQuestion | undefined => questions.find((question) => !is.nonEmptyString(mergedAnswer(question, local)));

export const useLocalBlockAnswers = (blockId: string): LocalAnswers =>
    useChatStore((state) => state.blockAnswers[blockId]);

// A question label is plain string but may carry inline ⟦…⟧ mention markup and
// markdown, so it is parsed and rendered through the mention markdown pipeline.
export const QuestionLabel: FunctionComponent<{ readonly label: string }> = ({ label }) => {
    const { markdown, mentions } = serializeRichTextToMarkdown(parseMentionMarkup(label));
    return <MentionMarkdown markdown={markdown} mentions={mentions} />;
};

// The composer card owns the active questionnaire prompt. The thread renders
// answered questions, whether backend-echoed or locally selected, as assistant
// prompts followed by user answer bubbles.
export const QuestionnaireThread: FunctionComponent<{
    readonly blockId: string;
    readonly questions: readonly QuestionnaireQuestion[];
}> = ({ blockId, questions }) => {
    const local = useLocalBlockAnswers(blockId);
    const visible = questions
        .map((question) => ({ answer: mergedAnswer(question, local), question }))
        .filter(({ answer }) => is.nonEmptyString(answer));
    if (visible.length === 0) return null;
    return (
        <div className="flex flex-col gap-s">
            {visible.map(({ answer, question }) => (
                <div key={`${blockId}-${question.actionId}`} className="flex flex-col gap-xs">
                    <AssistantBubble>
                        <AssistantBubble.Text fullWidth={true}>
                            <QuestionLabel label={question.label} />
                        </AssistantBubble.Text>
                    </AssistantBubble>
                    <UserBubble>{answer}</UserBubble>
                </div>
            ))}
        </div>
    );
};
